'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter, usePathname } from 'next/navigation';
import {
  Calendar,
  CalendarDays,
  Clock,
  Code2,
  CreditCard,
  Gift,
  LayoutDashboard,
  Search,
  Settings,
  Sparkles,
  UserPlus,
  Users,
  Plus,
  type LucideIcon,
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

interface CommandItem {
  id: string;
  label: string;
  hint?: string;
  icon: LucideIcon;
  /** Keywords appended to label for fuzzy matching. */
  keywords?: string;
  group: 'nav' | 'action';
  run: () => void;
}

/**
 * Linear-style global command palette (Cmd+K / Ctrl+K).
 *
 * Self-contained: own keyboard listener registers in a useEffect on mount
 * and is removed on unmount. Open state is local — no external store
 * because the trigger is purely keyboard-driven.
 *
 * Keyboard:
 *   - ↑ / ↓ : move highlight
 *   - Enter : run highlighted command
 *   - Esc   : close (handled by Dialog)
 *
 * Commands fall into two groups: `nav` (navigation) and `action` (mutating
 * primary actions). Matching is a case-insensitive substring on the joined
 * label + keywords string — adequate for ~12 entries.
 */
export function CommandPalette() {
  const router = useRouter();
  const pathname = usePathname();
  const t = useTranslations('admin.ui');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Global hotkey — Cmd+K on macOS, Ctrl+K elsewhere. Ignore when the user
  // is typing in a form field so it doesn't fight in-page shortcuts.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.key === 'k' || e.key === 'K')) return;
      if (!(e.metaKey || e.ctrlKey)) return;
      // On /admin/settings/* the settings-scoped SettingsSearch palette owns
      // Cmd+K. Yield to it so we don't open two palettes at once — this Radix
      // Dialog's `hideOthers` would aria-hide the wrapper that contains the
      // other (focused) palette, which assistive tech flags as a focus trap.
      if (pathname?.startsWith('/admin/settings')) return;
      e.preventDefault();
      setOpen((v) => !v);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [pathname]);

  // Reset query + highlight whenever the palette opens. Focus the input
  // on next tick so Radix has mounted the content.
  useEffect(() => {
    if (open) {
      /* eslint-disable react-hooks/set-state-in-effect -- reacting to the open toggle: focus mgmt + clean slate per open */
      setQuery('');
      setHighlight(0);
      /* eslint-enable react-hooks/set-state-in-effect */
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  const close = useCallback(() => setOpen(false), []);

  const commands = useMemo<CommandItem[]>(
    () => [
      // Navigation
      { id: 'nav-dashboard', label: t('cmdDashboard'), icon: LayoutDashboard, group: 'nav',
        keywords: 'home übersicht', run: () => { router.push('/admin'); close(); } },
      { id: 'nav-bookings', label: t('cmdBookings'), icon: CalendarDays, group: 'nav',
        keywords: 'reservations termine', run: () => { router.push('/admin/bookings'); close(); } },
      { id: 'nav-calendar', label: t('cmdCalendar'), icon: Calendar, group: 'nav',
        keywords: 'agenda planung', run: () => { router.push('/admin/calendar/bookings-calendar'); close(); } },
      { id: 'nav-services', label: t('cmdServices'), icon: Sparkles, group: 'nav',
        keywords: 'services angebote', run: () => { router.push('/admin/services'); close(); } },
      { id: 'nav-employees', label: t('cmdEmployees'), icon: Users, group: 'nav',
        keywords: 'team staff', run: () => { router.push('/admin/employees'); close(); } },
      { id: 'nav-hours', label: t('cmdHours'), icon: Clock, group: 'nav',
        keywords: 'hours schedule zeiten', run: () => { router.push('/admin/hours'); close(); } },
      { id: 'nav-settings', label: t('cmdSettings'), icon: Settings, group: 'nav',
        keywords: 'config settings', run: () => { router.push('/admin/settings'); close(); } },
      { id: 'nav-subscription', label: t('cmdSubscription'), icon: CreditCard, group: 'nav',
        keywords: 'billing payment subscription', run: () => { router.push('/admin/subscription'); close(); } },
      // Actions — deep-link with intent param so the target page opens its form.
      { id: 'act-new-booking', label: t('cmdNewBooking'), icon: Plus, group: 'action',
        hint: t('cmdHintBookings'), keywords: 'create add', run: () => { router.push('/admin/bookings?new=1'); close(); } },
      { id: 'act-new-service', label: t('cmdNewService'), icon: Plus, group: 'action',
        hint: t('cmdHintServices'), keywords: 'create add service', run: () => { router.push('/admin/services?new=1'); close(); } },
      { id: 'act-invite-employee', label: t('cmdInviteEmployee'), icon: UserPlus, group: 'action',
        hint: t('cmdHintTeam'), keywords: 'invite team add', run: () => { router.push('/admin/employees?invite=1'); close(); } },
      { id: 'act-embed', label: t('cmdEmbedWidget'), icon: Code2, group: 'action',
        hint: t('cmdHintSettings'), keywords: 'iframe embed widget snippet wordpress wix webflow integration',
        run: () => { router.push('/admin/settings/embed'); close(); } },
      { id: 'act-referrals', label: t('cmdShareReferral'), icon: Gift, group: 'action',
        hint: t('cmdHintGrowth'), keywords: 'referral empfehlen rabatt gratis monat freunde',
        run: () => { router.push('/admin/referrals'); close(); } },
    ],
    [router, close, t]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter((cmd) =>
      `${cmd.label} ${cmd.keywords ?? ''}`.toLowerCase().includes(q)
    );
  }, [query, commands]);

  // Clamp highlight whenever the filtered set changes.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- clamp the highlight into the new result range
    setHighlight((h) => Math.min(h, Math.max(0, filtered.length - 1)));
  }, [filtered.length]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(filtered.length - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      filtered[highlight]?.run();
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="overflow-hidden p-0 sm:max-w-[560px] [&>button.absolute]:hidden">
        <DialogTitle className="sr-only">{t('commandPalette')}</DialogTitle>
        <div className="flex items-center gap-3 border-b border-gray-100 px-4">
          <Search className="h-4 w-4 flex-shrink-0 text-gray-400" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setHighlight(0);
            }}
            onKeyDown={onKeyDown}
            placeholder={t('cmdSearchPlaceholder')}
            className="w-full bg-transparent py-3.5 text-sm placeholder:text-gray-400 focus:outline-none"
            aria-label={t('cmdSearchAria')}
          />
          <kbd className="hidden flex-shrink-0 rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 sm:inline-flex">
            ESC
          </kbd>
        </div>

        <div className="max-h-[400px] overflow-y-auto py-2">
          {filtered.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-gray-500">
              {t('cmdNoResults', { query })}
            </div>
          ) : (
            (['nav', 'action'] as const).map((group) => {
              const items = filtered.filter((c) => c.group === group);
              if (items.length === 0) return null;
              return (
                <div key={group}>
                  <div className="px-4 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-400">
                    {group === 'nav' ? t('cmdGroupNav') : t('cmdGroupActions')}
                  </div>
                  <ul role="listbox">
                    {items.map((cmd) => {
                      const flatIndex = filtered.indexOf(cmd);
                      const isHighlight = flatIndex === highlight;
                      const Icon = cmd.icon;
                      return (
                        <li
                          key={cmd.id}
                          role="option"
                          aria-selected={isHighlight}
                          onMouseEnter={() => setHighlight(flatIndex)}
                          onClick={cmd.run}
                          className={`mx-2 flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-sm ${
                            isHighlight ? 'bg-gray-100 text-gray-950' : 'text-gray-700'
                          }`}
                        >
                          <Icon className="h-4 w-4 flex-shrink-0 text-gray-500" />
                          <span className="flex-1 truncate">{cmd.label}</span>
                          {cmd.hint && (
                            <span className="text-xs text-gray-400">{cmd.hint}</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })
          )}
        </div>

        <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50/60 px-4 py-2 text-[11px] text-gray-500">
          <span className="inline-flex items-center gap-1">
            <kbd className="rounded border border-gray-200 bg-white px-1 py-0.5 font-mono">↑</kbd>
            <kbd className="rounded border border-gray-200 bg-white px-1 py-0.5 font-mono">↓</kbd>
            {t('cmdNavigate')}
          </span>
          <span className="inline-flex items-center gap-1">
            <kbd className="rounded border border-gray-200 bg-white px-1 py-0.5 font-mono">↵</kbd>
            {t('cmdOpen')}
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
